import { useEffect, useRef, useState } from "react";
import { Trade, api, isError } from "../api";
import { nativeNotify } from "../tauri";

const POLL_MS = 2000;

/** Polls /trades and fires native desktop notifications (in Tauri) on
 * status transitions to "filled" / "closed". The first tick seeds the
 * baseline so pre-existing trades don't spam toasts on app launch. */
export function useTradesPoll(): Trade[] {
  const [trades, setTrades] = useState<Trade[]>([]);
  const seen = useRef<Map<number, string> | null>(null);

  useEffect(() => {
    let cancelled = false;

    function maybeNotify(list: Trade[]) {
      if (seen.current === null) {
        seen.current = new Map(list.map((t) => [t.id, t.status]));
        return;
      }
      for (const t of list) {
        const prev = seen.current.get(t.id);
        if (prev !== t.status) {
          if (t.status === "filled") {
            void nativeNotify(
              "Trade filled",
              `${t.direction.toUpperCase()} ${t.symbol} @ ${t.fill_price ?? "?"}`,
            );
          } else if (t.status === "closed") {
            const pnl = t.pnl_currency;
            void nativeNotify(
              "Trade closed",
              `${t.symbol} P/L ${pnl == null ? "?" : (pnl >= 0 ? "+" : "") + pnl.toFixed(2)}`,
            );
          }
          seen.current.set(t.id, t.status);
        }
      }
    }

    async function tick() {
      const r = await api.trades(25);
      if (cancelled) return;
      const list = isError(r) ? [] : r.trades;
      setTrades(list);
      maybeNotify(list);
    }
    void tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return trades;
}
